import type { ActionResult, Post, TasteView } from "@brandwall/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, post, put } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

export function Taste() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const canEdit = workspace.role !== "viewer";
  const qc = useQueryClient();
  const toast = useToast();
  const taste = useQuery({
    queryKey: ["taste", ws],
    queryFn: () => api<TasteView>(`/v1/w/${ws}/taste`),
  });
  const candidates = useQuery({
    queryKey: ["taste-candidates", ws],
    queryFn: () => api<{ posts: Post[] }>(`/v1/w/${ws}/taste/candidates`),
  });
  const best = useQuery({
    queryKey: ["posts", ws, "to_review", "best"],
    queryFn: () => api<{ posts: Post[] }>(`/v1/w/${ws}/posts?tab=to_review&limit=1`),
  });
  const [rules, setRules] = useState("");
  const [competitors, setCompetitors] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (taste.data) {
      setRules(taste.data.rules);
      setCompetitors(taste.data.competitors.join(", "));
    }
  }, [taste.data]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      put<TasteView & { result: ActionResult }>(`/v1/w/${ws}/taste`, body),
    onSuccess: (d) => {
      qc.setQueryData(["taste", ws], d);
      toast(d.result.summary);
      setError(null);
      qc.invalidateQueries({ queryKey: ["posts", ws] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Something went wrong."),
  });
  const mark = useMutation({
    mutationFn: (b: { postId: string; kind: "love" | "never" | null }) =>
      post<TasteView>(`/v1/w/${ws}/taste/examples`, b),
    onSuccess: (d) => qc.setQueryData(["taste", ws], d),
  });

  const t = taste.data;
  if (!t) return <p className="muted">Loading…</p>;
  const kindOf = (id: string) => t.examples.find((e) => e.postId === id)?.kind ?? null;
  const sample = best.data?.posts[0];

  return (
    <div className="split">
      <section className="card stack">
        <div>
          <h2>Show Brandwall what you like</h2>
          <p className="muted">
            Pick a few posts you love, and a few you'd never show. Brandwall then ranks posts like
            the ones you love higher in your inbox.
          </p>
        </div>
        {candidates.data?.posts.length === 0 && (
          <p className="muted">
            Posts show up here once Brandwall has collected some from Instagram. Connect Instagram
            or add hashtags on Sources to get started.
          </p>
        )}
        <div className="examples">
          {candidates.data?.posts.map((p) => {
            const k = kindOf(p.id);
            return (
              <div key={p.id} className={`example ${k ?? ""}`}>
                <div className="pic">
                  {p.previewUrl && (
                    <img
                      src={p.previewUrl}
                      alt={`Post by @${p.creator || "a creator"}`}
                      loading="lazy"
                    />
                  )}
                </div>
                {canEdit && (
                  <div className="row">
                    <button
                      type="button"
                      className="toggle love"
                      aria-pressed={k === "love"}
                      aria-label={`Love the post by @${p.creator || "a creator"}`}
                      onClick={() =>
                        mark.mutate({ postId: p.id, kind: k === "love" ? null : "love" })
                      }
                    >
                      ♥
                    </button>
                    <button
                      type="button"
                      className="toggle never"
                      aria-pressed={k === "never"}
                      aria-label={`Never show posts like the one by @${p.creator || "a creator"}`}
                      onClick={() =>
                        mark.mutate({ postId: p.id, kind: k === "never" ? null : "never" })
                      }
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="stack sticky-side">
        <section className="card stack">
          <div>
            <h2>Rules</h2>
            <p className="muted">
              Tell Brandwall what a good post looks like, the way you'd tell a colleague.
            </p>
          </div>
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          <label className="field">
            <span className="sr-only">Your rules</span>
            <textarea
              className="input"
              value={rules}
              disabled={!canEdit}
              placeholder="Always show the product. Skip food and screenshots."
              onChange={(e) => setRules(e.target.value)}
            />
          </label>
          {t.chips.length > 0 && (
            <div className="chips" role="list" aria-label="Rules as Brandwall understood them">
              {t.chips.map((c) => (
                <span key={`${c.kind}-${c.label}`} role="listitem" className={`chip ${c.kind}`}>
                  {c.kind === "avoid" ? "✕" : "✓"} {c.label}
                </span>
              ))}
            </div>
          )}
          <label className="field">
            Competitor brands
            <input
              className="input"
              value={competitors}
              disabled={!canEdit}
              placeholder="Brands you never want on your wall, separated by commas"
              onChange={(e) => setCompetitors(e.target.value)}
            />
          </label>
          {canEdit && (
            <button
              type="button"
              className="btn"
              disabled={save.isPending}
              onClick={() =>
                save.mutate({
                  rules,
                  competitors: competitors
                    .split(",")
                    .map((c) => c.trim())
                    .filter(Boolean),
                })
              }
            >
              {save.isPending ? "Saving…" : "Save rules"}
            </button>
          )}
          <label className="field">
            Mark posts as Top pick from this score
            <div className="row">
              <input
                type="range"
                min={50}
                max={100}
                step={1}
                value={t.threshold}
                disabled={!canEdit}
                aria-label="Mark posts as Top pick from this score"
                onChange={(e) =>
                  qc.setQueryData(["taste", ws], { ...t, threshold: Number(e.target.value) })
                }
                onMouseUp={() => save.mutate({ threshold: t.threshold })}
                onKeyUp={() => save.mutate({ threshold: t.threshold })}
                onTouchEnd={() => save.mutate({ threshold: t.threshold })}
                style={{ flex: 1 }}
              />
              <b>{t.threshold}</b>
            </div>
          </label>
        </section>

        <section className="card stack">
          <div className="row">
            <h2 className="spacer">How a new post is scored</h2>
            {sample?.topPick && <span className="pill berry mono">TOP PICK</span>}
          </div>
          {sample?.scores ? (
            <div className="row" style={{ alignItems: "flex-start", gap: 16 }}>
              {sample.previewUrl && <img className="thumb lg" src={sample.previewUrl} alt="" />}
              <div className="bars spacer">
                {(
                  [
                    ["Quality", sample.scores.quality],
                    ["Product", sample.scores.product],
                    ["Brand fit", sample.scores.brandFit],
                  ] as const
                ).map(([label, v]) => (
                  <Bar key={label} label={label} value={v} />
                ))}
              </div>
            </div>
          ) : (
            <p className="muted">Every new post gets a score for quality, product and brand fit.</p>
          )}
          {sample?.scoreReason && <p className="small muted">{sample.scoreReason}</p>}
          <p className="small muted">
            {t.engine === "claude"
              ? "Brandwall's AI looks at every photo and reads its caption."
              : "Brandwall checks sharpness, light and colour, and reads the caption. It doesn't look at what's in the photo."}
          </p>
        </section>
      </div>
    </div>
  );
}

function Bar({ label, value }: { label: string; value: number }) {
  return (
    <>
      <span className="muted">{label}</span>
      <span
        className="bar"
        role="meter"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <i style={{ width: `${value}%` }} />
      </span>
      <b style={{ textAlign: "right" }}>{value}</b>
    </>
  );
}
